import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { AdminApiConfig } from '../api/baseAdminApiClient';
import { AgentAdminApiClient } from '../api/agentAdminApiClient';
import { CredentialAdminApiClient } from '../api/credentialAdminApiClient';
import { TeamAdminApiClient } from '../api/teamAdminApiClient';
import { IdentityKeyAdminApiClient } from '../api/identityKeyAdminApiClient';
import { TrustedDidAdminApiClient } from '../api/trustedDidAdminApiClient';
import { AgentTemplateAdminApiClient } from '../api/agentTemplateAdminApiClient';
import { AgentRuleAdminApiClient } from '../api/agentRuleAdminApiClient';
import { AgentHookAdminApiClient } from '../api/agentHookAdminApiClient';
import { AgentCapabilityAdminApiClient } from '../api/agentCapabilityAdminApiClient';
import { AgentQuotaAdminApiClient } from '../api/agentQuotaAdminApiClient';
import { AgentPolicyAdminApiClient } from '../api/agentPolicyAdminApiClient';
import { AgentPaymentRuleAdminApiClient } from '../api/agentPaymentRuleAdminApiClient';
import { AgentDataSampleAdminApiClient } from '../api/agentDataSampleAdminApiClient';
import { AgentScopeAdminApiClient } from '../api/agentScopeAdminApiClient';
import { AgentVersionAdminApiClient } from '../api/agentVersionAdminApiClient';
import type { CredentialFormRenderer, CredentialOAuthConnect } from '../types';
import { defaultCredentialFormRenderer } from '../components/Credential/defaultCredentialFormRenderer';

interface AiAgentAdminApiClients {
  agent: AgentAdminApiClient;
  credential: CredentialAdminApiClient;
  team: TeamAdminApiClient;
  identityKey: IdentityKeyAdminApiClient;
  trustedDid: TrustedDidAdminApiClient;
  agentTemplate: AgentTemplateAdminApiClient;
  agentRule: AgentRuleAdminApiClient;
  agentHook: AgentHookAdminApiClient;
  agentCapability: AgentCapabilityAdminApiClient;
  agentQuota: AgentQuotaAdminApiClient;
  agentPolicy: AgentPolicyAdminApiClient;
  agentPaymentRule: AgentPaymentRuleAdminApiClient;
  agentDataSample: AgentDataSampleAdminApiClient;
  agentScope: AgentScopeAdminApiClient;
  agentVersion: AgentVersionAdminApiClient;
  formRenderer: CredentialFormRenderer;
  oauthConnect?: CredentialOAuthConnect;
}

const AiAgentAdminApiContext = createContext<AiAgentAdminApiClients | null>(null);

export interface AiAgentAdminApiProviderProps extends AdminApiConfig {
  children: ReactNode;
  /** Optional pluggable dynamic credential-fields renderer (e.g. an Atlas Forms adapter). Omit to
   *  get today's exact plain-HTML rendering (`defaultCredentialFormRenderer`) — this is the
   *  default for every existing consumer (octopus-admin, did-admin, octopus-a2a) and changes
   *  nothing for them. */
  formRenderer?: CredentialFormRenderer;
  /** Optional pluggable OAuth2 "Connect" popup control, shown only for OAuth-type credentials.
   *  Omit to keep today's plain-text note-and-paste behavior (no Connect button). */
  oauthConnect?: CredentialOAuthConnect;
}

/**
 * Wrap any consuming app's Agent/Credential/Team admin surface in this once — every component in
 * this package shares the same three configured clients instead of each needing baseUrl/auth
 * wiring passed down by hand. Mirrors AiAgentCatalogApiProvider's shape exactly (same package
 * family, same precedent).
 */
export function AiAgentAdminApiProvider({ children, formRenderer, oauthConnect, ...config }: AiAgentAdminApiProviderProps) {
  const clients = useMemo<AiAgentAdminApiClients>(
    () => ({
      agent: new AgentAdminApiClient(config),
      credential: new CredentialAdminApiClient(config),
      team: new TeamAdminApiClient(config),
      identityKey: new IdentityKeyAdminApiClient(config),
      trustedDid: new TrustedDidAdminApiClient(config),
      agentTemplate: new AgentTemplateAdminApiClient(config),
      agentRule: new AgentRuleAdminApiClient(config),
      agentHook: new AgentHookAdminApiClient(config),
      agentCapability: new AgentCapabilityAdminApiClient(config),
      agentQuota: new AgentQuotaAdminApiClient(config),
      agentPolicy: new AgentPolicyAdminApiClient(config),
      agentPaymentRule: new AgentPaymentRuleAdminApiClient(config),
      agentDataSample: new AgentDataSampleAdminApiClient(config),
      agentScope: new AgentScopeAdminApiClient(config),
      agentVersion: new AgentVersionAdminApiClient(config),
      formRenderer: formRenderer ?? defaultCredentialFormRenderer,
      oauthConnect,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.baseUrl, config.tenantId]
  );
  // Config (notably getAuthToken) is refreshed every render via updateConfig, so a token/closure
  // change is never silently frozen out — see AiAgentCatalogApiClient.updateConfig's identical
  // rationale.
  clients.agent.updateConfig(config);
  clients.credential.updateConfig(config);
  clients.team.updateConfig(config);
  clients.identityKey.updateConfig(config);
  clients.trustedDid.updateConfig(config);
  clients.agentTemplate.updateConfig(config);
  clients.agentRule.updateConfig(config);
  clients.agentHook.updateConfig(config);
  clients.agentCapability.updateConfig(config);
  clients.agentQuota.updateConfig(config);
  clients.agentPolicy.updateConfig(config);
  clients.agentPaymentRule.updateConfig(config);
  clients.agentDataSample.updateConfig(config);
  clients.agentScope.updateConfig(config);
  clients.agentVersion.updateConfig(config);
  // formRenderer/oauthConnect are refreshed every render too, mirroring the updateConfig pattern
  // above — a parent re-render passing a new inline function reference is picked up without
  // forcing the API clients themselves to be reconstructed.
  clients.formRenderer = formRenderer ?? defaultCredentialFormRenderer;
  clients.oauthConnect = oauthConnect;

  return <AiAgentAdminApiContext.Provider value={clients}>{children}</AiAgentAdminApiContext.Provider>;
}

function useClients(): AiAgentAdminApiClients {
  const clients = useContext(AiAgentAdminApiContext);
  if (!clients) {
    throw new Error('useAgentAdminApiClient/useCredentialAdminApiClient/useTeamAdminApiClient must be used within an <AiAgentAdminApiProvider>.');
  }
  return clients;
}

export function useAgentAdminApiClient(): AgentAdminApiClient {
  return useClients().agent;
}

export function useCredentialAdminApiClient(): CredentialAdminApiClient {
  return useClients().credential;
}

export function useTeamAdminApiClient(): TeamAdminApiClient {
  return useClients().team;
}

export function useIdentityKeyAdminApiClient(): IdentityKeyAdminApiClient {
  return useClients().identityKey;
}

export function useTrustedDidAdminApiClient(): TrustedDidAdminApiClient {
  return useClients().trustedDid;
}

export function useAgentTemplateAdminApiClient(): AgentTemplateAdminApiClient {
  return useClients().agentTemplate;
}

export function useAgentRuleAdminApiClient(): AgentRuleAdminApiClient {
  return useClients().agentRule;
}

export function useAgentHookAdminApiClient(): AgentHookAdminApiClient {
  return useClients().agentHook;
}

export function useAgentCapabilityAdminApiClient(): AgentCapabilityAdminApiClient {
  return useClients().agentCapability;
}

export function useAgentQuotaAdminApiClient(): AgentQuotaAdminApiClient {
  return useClients().agentQuota;
}

export function useAgentPolicyAdminApiClient(): AgentPolicyAdminApiClient {
  return useClients().agentPolicy;
}

export function useAgentPaymentRuleAdminApiClient(): AgentPaymentRuleAdminApiClient {
  return useClients().agentPaymentRule;
}

export function useAgentDataSampleAdminApiClient(): AgentDataSampleAdminApiClient {
  return useClients().agentDataSample;
}

export function useAgentScopeAdminApiClient(): AgentScopeAdminApiClient {
  return useClients().agentScope;
}

export function useAgentVersionAdminApiClient(): AgentVersionAdminApiClient {
  return useClients().agentVersion;
}

/** Resolves to whichever `formRenderer` the nearest `<AiAgentAdminApiProvider>` was given, or the
 *  built-in `defaultCredentialFormRenderer` when none was supplied. Always resolve as a JSX
 *  component (`<FormRenderer .../>`), never call it as a bare function — a hooks-based custom
 *  renderer must be mounted like any other component, not invoked conditionally mid-render. */
export function useCredentialFormRenderer(): CredentialFormRenderer {
  return useClients().formRenderer;
}

/** Resolves to whichever `oauthConnect` the nearest `<AiAgentAdminApiProvider>` was given, or
 *  `undefined` when none was supplied (CredentialEditor falls back to its plain-text note in that
 *  case). Same JSX-component resolution rule as `useCredentialFormRenderer`. */
export function useCredentialOAuthConnect(): CredentialOAuthConnect | undefined {
  return useClients().oauthConnect;
}
